import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Cake,
  CalendarHeart,
  Filter,
  GraduationCap,
  Home,
  Music,
  Search,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { getTemplates } from "../api/templates";
import TemplateCard from "../components/TemplateCard";
import { useAuth } from "../contexts/AuthContext";
import LoginModal from "../components/LoginModal";

const EVENT_CATEGORIES = [
  {
    id: "wedding",
    title: "Wedding",
    description: "Grand, multi-function invitation films for ceremonies and celebrations.",
    icon: CalendarHeart,
    keywords: ["wedding", "marriage", "bride", "groom", "reception", "haldi", "mehendi", "sangeet"],
  },
  {
    id: "birthday",
    title: "Birthday",
    description: "Playful and elegant video invitations for milestones, kids, and parties.",
    icon: Cake,
    keywords: ["birthday", "party", "celebration", "cake"],
  },
  {
    id: "baby-shower",
    title: "Baby Shower",
    description: "Warm, heartfelt invitation films for blessings, family moments, and registry-led events.",
    icon: Sparkles,
    keywords: ["baby", "shower", "newborn", "blessing"],
  },
  {
    id: "graduation",
    title: "Graduation",
    description: "Proud, polished invitation videos for achievement moments and gatherings.",
    icon: GraduationCap,
    keywords: ["graduation", "graduate", "school", "college", "convocation"],
  },
  {
    id: "housewarming",
    title: "Housewarming",
    description: "Elegant invites for griha pravesh, home blessings, and hosting family.",
    icon: Home,
    keywords: ["housewarming", "home", "griha", "pravesh", "house"],
  },
  {
    id: "cultural-programs",
    title: "Cultural Programs",
    description: "Vibrant invitation films for dance shows, music nights, school functions, and community events.",
    icon: Music,
    keywords: ["cultural", "program", "performance", "dance", "music", "festival", "school", "community"],
  },
  {
    id: "engagement",
    title: "Engagement",
    description: "Romantic motion-led invites for rings, families, and pre-wedding events.",
    icon: Users,
    keywords: ["engagement", "ring", "couple", "proposal"],
  },
  {
    id: "corporate",
    title: "Corporate",
    description: "Modern, professional invitation pages for launches, dinners, and milestone events.",
    icon: Briefcase,
    keywords: ["corporate", "company", "business", "launch", "annual", "conference"],
  },
];

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "name-asc", label: "Name: A to Z" },
  { value: "name-desc", label: "Name: Z to A" },
];

const BROWSE_PROMISES = [
  {
    title: "Autoplay video previews",
    text: "Judge motion, pacing, and mood before you commit to a design.",
  },
  {
    title: "Event-aware filtering",
    text: "Start with the occasion so the next event form feels relevant.",
  },
  {
    title: "Guest journey ready",
    text: "Wedding templates can later support subevents, groups, and private media.",
  },
];

function normalizeEventType(value) {
  const normalizedValue = `${value || ""}`.toLowerCase().replace(/_/g, "-");
  return normalizedValue === "marriage" ? "wedding" : normalizedValue;
}

function resolveTemplateCategory(template) {
  const eventType = normalizeEventType(template?.event_type);
  if (eventType) {
    return eventType;
  }

  const lowerName = `${template?.name || ""}`.toLowerCase();
  const matchedCategory = EVENT_CATEGORIES.find((category) =>
    category.keywords.some((keyword) => lowerName.includes(keyword))
  );
  return matchedCategory?.id || "featured";
}

function getTemplateSearchText(template) {
  return [
    template?.name,
    template?.event_type,
    template?.category,
    template?.description,
    ...(Array.isArray(template?.style_tags) ? template.style_tags : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function countTemplatesForCategory(templates, categoryId) {
  const category = EVENT_CATEGORIES.find((item) => item.id === categoryId);
  if (!category) {
    return 0;
  }

  return templates.filter((template) => {
    if (normalizeEventType(template?.event_type || template?.category) === categoryId) {
      return true;
    }

    const searchText = getTemplateSearchText(template);
    return category.keywords.some((keyword) => searchText.includes(keyword));
  }).length;
}

function filterTemplatesByEventType(templates, eventType) {
  if (!eventType || eventType === "all") {
    return templates;
  }

  const normalizedEventType = normalizeEventType(eventType);
  const category = EVENT_CATEGORIES.find((item) => item.id === normalizedEventType);
  if (!category) {
    return templates;
  }

  const metadataMatches = templates.filter(
    (template) => normalizeEventType(template.event_type) === normalizedEventType
  );

  if (metadataMatches.length) {
    return metadataMatches;
  }

  const fallbackMatches = templates.filter((template) =>
    category.keywords.some((keyword) =>
      `${template.name || ""}`.toLowerCase().includes(keyword)
    )
  );

  return fallbackMatches.length ? fallbackMatches : templates;
}

export default function TemplatesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const initialEventType = normalizeEventType(location.state?.eventType || "all");
  const initialSearchQuery = location.state?.searchQuery || "";

  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [selectedEventType, setSelectedEventType] = useState(initialEventType);
  const [selectedSort, setSelectedSort] = useState("featured");
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoading(true);
        const data = await getTemplates();
        setTemplates(data || []);
      } catch (error) {
        console.error("Failed to load templates:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const selectedCategory = useMemo(
    () => EVENT_CATEGORIES.find((category) => category.id === selectedEventType),
    [selectedEventType]
  );

  const filteredTemplates = useMemo(() => {
    const eventFilteredTemplates = filterTemplatesByEventType(templates, selectedEventType);
    const normalizedQuery = searchQuery.trim().toLowerCase();

    const queryFilteredTemplates = normalizedQuery
      ? eventFilteredTemplates.filter((template) =>
          getTemplateSearchText(template).includes(normalizedQuery)
        )
      : eventFilteredTemplates;

    if (selectedSort === "featured") {
      return [...queryFilteredTemplates].sort((a, b) => {
        const featuredWeight = Number(Boolean(b.is_featured)) - Number(Boolean(a.is_featured));
        if (featuredWeight !== 0) {
          return featuredWeight;
        }
        const premiumWeight = Number(Boolean(b.is_premium)) - Number(Boolean(a.is_premium));
        if (premiumWeight !== 0) {
          return premiumWeight;
        }
        return `${a.name || ""}`.localeCompare(`${b.name || ""}`);
      });
    }

    if (selectedSort === "name-asc") {
      return [...queryFilteredTemplates].sort((a, b) =>
        `${a.name || ""}`.localeCompare(`${b.name || ""}`)
      );
    }

    if (selectedSort === "name-desc") {
      return [...queryFilteredTemplates].sort((a, b) =>
        `${b.name || ""}`.localeCompare(`${a.name || ""}`)
      );
    }

    return queryFilteredTemplates;
  }, [searchQuery, selectedEventType, selectedSort, templates]);

  const featuredTemplates = useMemo(() => {
    const explicitFeaturedTemplates = filteredTemplates.filter((template) => template.is_featured);
    return (explicitFeaturedTemplates.length ? explicitFeaturedTemplates : filteredTemplates).slice(0, 3);
  }, [filteredTemplates]);

  const handleUseTemplate = (templateId) => {
    setSelectedTemplateId(templateId);

    if (isAuthenticated) {
      navigate(`/editor/${templateId}`, {
        state: {
          mode: "event",
          eventType: selectedEventType === "all" ? undefined : selectedEventType,
        },
      });
      return;
    }

    setShowLogin(true);
  };

  const handleLoginSuccess = () => {
    if (!selectedTemplateId) return;

    navigate(`/editor/${selectedTemplateId}`, {
      state: {
        mode: "event",
        eventType: selectedEventType === "all" ? undefined : selectedEventType,
      },
    });
  };

  const clearFilters = () => {
    setSelectedEventType("all");
    setSearchQuery("");
    setSelectedSort("featured");
  };

  return (
    <div className="min-h-screen bg-[#fff8fb] text-[#261c25]">
      <section className="border-b border-[#f1dfe9] bg-[linear-gradient(180deg,#fff8fb_0%,#fff3f8_100%)]">
        <div className="mx-auto w-full max-w-[1380px] px-4 py-10 md:px-8 xl:px-10 xl:py-12">
          <div className="flex flex-col gap-8 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                Browse invitation films
              </p>
              <h1 className="mt-3 font-[Georgia] text-[clamp(2rem,4vw,3.7rem)] font-black leading-tight text-[#241824]">
                {selectedCategory ? `${selectedCategory.title} templates` : "Find the right invitation film"}
              </h1>
              <p className="mt-4 text-base leading-7 text-[#6f5a67] md:text-lg">
                Preview the video style first, then choose the template that best matches your celebration.
                After selection, the guided flow handles event details, editing, guests, and sharing.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 rounded-full border border-[#edd4e1] bg-white px-5 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
              >
                Change Occasion
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-2 rounded-full bg-[#fff0f7] px-5 py-3 text-sm font-bold text-[#9b336a] transition-colors hover:bg-[#ffe4f2]"
              >
                Reset Filters
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 items-center gap-3 rounded-full border border-[#efcddd] bg-white px-5 py-3 shadow-lg shadow-[#f3d7e4]/70">
              <Search className="h-5 w-5 text-[#b13771]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by template name, mood, or celebration style"
                className="w-full bg-transparent text-sm text-[#3d2e38] outline-none placeholder:text-[#b29aa8] md:text-base"
              />
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#edd4e1] bg-white px-5 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
              >
                <Filter size={18} />
                Filters
              </button>

              <div className="flex items-center gap-3 rounded-full border border-[#edd4e1] bg-white px-4 py-3">
                <span className="text-sm font-bold text-[#8b6479]">Sort</span>
                <select
                  value={selectedSort}
                  onChange={(event) => setSelectedSort(event.target.value)}
                  className="bg-transparent text-sm font-semibold text-[#442634] outline-none"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setSelectedEventType("all")}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                selectedEventType === "all"
                  ? "bg-[#ff0099] text-white shadow-md shadow-[#ff0099]/20"
                  : "bg-white text-[#834d69] hover:bg-[#fff0f7]"
              }`}
            >
              All Occasions
            </button>
            {EVENT_CATEGORIES.map((category) => {
              const Icon = category.icon;
              const isActive = selectedEventType === category.id;
              const templateCount = countTemplatesForCategory(templates, category.id);

              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setSelectedEventType(category.id)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                    isActive
                      ? "bg-[#ff0099] text-white shadow-md shadow-[#ff0099]/20"
                      : "bg-white text-[#834d69] hover:bg-[#fff0f7]"
                  }`}
                >
                  <Icon size={16} />
                  {category.title}
                  {templateCount ? (
                    <span className={isActive ? "text-white/75" : "text-[#b889a2]"}>
                      {templateCount}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            {BROWSE_PROMISES.map((item) => (
              <div
                key={item.title}
                className="rounded-[24px] border border-[#efd8e4] bg-white/80 p-4 shadow-sm"
              >
                <strong className="block text-sm font-black text-[#4a2338]">{item.title}</strong>
                <span className="mt-1 block text-xs leading-5 text-[#806777]">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1380px] px-4 py-10 md:px-8 xl:px-10">
        <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-[30px] border border-[#edd9e4] bg-white p-6 shadow-[0_18px_50px_rgba(86,43,66,0.08)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                  Results
                </p>
                <h2 className="mt-2 text-2xl font-black text-[#241824]">
                  {filteredTemplates.length} template{filteredTemplates.length === 1 ? "" : "s"} ready to preview
                </h2>
                <p className="mt-2 text-sm leading-6 text-[#806777]">
                  {selectedCategory
                    ? `Showing ${selectedCategory.title.toLowerCase()} invitation films.`
                    : "Showing templates across all occasions."}
                  {searchQuery.trim() ? ` Search: "${searchQuery.trim()}".` : ""}
                </p>
              </div>
              {selectedCategory ? (
                <p className="max-w-md text-sm leading-6 text-[#735f6d]">
                  {selectedCategory.description}
                </p>
              ) : (
                <p className="max-w-md text-sm leading-6 text-[#735f6d]">
                  Browse across occasions, preview the invitation film, and start with the design that fits your event best.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-[30px] border border-[#edd9e4] bg-[#fff2f8] p-6 shadow-[0_18px_50px_rgba(86,43,66,0.06)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
              How to choose
            </p>
            <ul className="mt-3 space-y-3 text-sm leading-6 text-[#6f5a67]">
              <li>Preview the video first to judge pacing, mood, and storytelling.</li>
              <li>Use the occasion chips to keep the browsing results relevant.</li>
              <li>Pick the template only after the film style feels right for your guests.</li>
              <li>For weddings, choose the overall style first; subevents and guest-specific pages come later.</li>
            </ul>
          </div>
        </div>

        {featuredTemplates.length ? (
          <div className="mt-8">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                  Featured previews
                </p>
                <h3 className="mt-2 text-[clamp(1.35rem,2vw,1.9rem)] font-black text-[#251a24]">
                  Start with these standout invitation films
                </h3>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {featuredTemplates.map((template) => (
                <TemplateCard
                  key={`featured-${template.id}`}
                  template={template}
                  categoryLabel={selectedCategory?.title || "Featured Collection"}
                  onUseTemplate={() => handleUseTemplate(template.id)}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-10">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                All matching templates
              </p>
              <h3 className="mt-2 text-[clamp(1.35rem,2vw,1.9rem)] font-black text-[#251a24]">
                Explore and preview before you choose
              </h3>
            </div>
          </div>

          {isLoading ? (
            <div className="rounded-[30px] border border-[#edd9e4] bg-white p-10 text-center text-[#8a7080] shadow-sm">
              Loading invitation films...
            </div>
          ) : filteredTemplates.length ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  categoryLabel={
                    EVENT_CATEGORIES.find((category) => category.id === resolveTemplateCategory(template))?.title ||
                    (selectedCategory?.title ?? "Featured Collection")
                  }
                  onUseTemplate={() => handleUseTemplate(template.id)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-[30px] border border-dashed border-[#ebd1df] bg-white p-10 text-center">
              <h3 className="text-xl font-black text-[#2d1c28]">No templates matched those filters</h3>
              <p className="mt-3 text-sm leading-6 text-[#735f6d]">
                Try a broader search, switch the occasion, or reset the filters to explore more invitation styles.
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#ff0099] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-black"
              >
                Reset and browse all
                <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </section>

      {filterOpen ? (
        <div className="fixed inset-0 z-50 bg-black/45" onClick={() => setFilterOpen(false)}>
          <div
            className="ml-auto flex h-full w-full max-w-[360px] flex-col bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                  Quick filters
                </p>
                <h2 className="mt-2 text-2xl font-black text-[#241824]">Refine your browse</h2>
              </div>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="rounded-full p-2 text-[#8f6379] transition-colors hover:bg-[#fff0f7] hover:text-[#ff0099]"
                aria-label="Close filters"
              >
                <X size={22} />
              </button>
            </div>

            <div className="mt-8 space-y-4">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#9d7087]">
                Occasion
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedEventType("all")}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                    selectedEventType === "all"
                      ? "bg-[#ff0099] text-white"
                      : "bg-[#fff5fa] text-[#8c5f76] hover:bg-[#ffe8f3]"
                  }`}
                >
                  All Occasions
                </button>
                {EVENT_CATEGORIES.map((category) => (
                  <button
                    key={`drawer-${category.id}`}
                    type="button"
                    onClick={() => setSelectedEventType(category.id)}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                      selectedEventType === category.id
                        ? "bg-[#ff0099] text-white"
                        : "bg-[#fff5fa] text-[#8c5f76] hover:bg-[#ffe8f3]"
                    }`}
                  >
                    {category.title}
                    {countTemplatesForCategory(templates, category.id) ? (
                      <span className="ml-1 opacity-70">
                        {countTemplatesForCategory(templates, category.id)}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-auto flex gap-3 pt-8">
              <button
                type="button"
                onClick={clearFilters}
                className="flex-1 rounded-full border border-[#ecd2df] px-4 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 rounded-full bg-[#ff0099] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-black"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showLogin ? (
        <LoginModal
          defaultValue=""
          onClose={() => setShowLogin(false)}
          onSuccess={handleLoginSuccess}
        />
      ) : null}
    </div>
  );
}
